import React, { useEffect } from "react";
import { Helmet } from "react-helmet-async";
import { Box, Typography } from "@mui/material";
import GradientIcon from "../components/GradientIcon";
import HaveQuestions from "../components/HaveQuestions";
import ManageSearchOutlinedIcon from "@mui/icons-material/ManageSearchOutlined";
import AutorenewOutlinedIcon from "@mui/icons-material/AutorenewOutlined";
import SettingsOutlinedIcon from "@mui/icons-material/SettingsOutlined";
import VerifiedUserOutlinedIcon from "@mui/icons-material/VerifiedUserOutlined";
import PersonAddAlt1OutlinedIcon from "@mui/icons-material/PersonAddAlt1Outlined";
import SyncAltOutlinedIcon from "@mui/icons-material/SyncAltOutlined";
import ApiOutlinedIcon from "@mui/icons-material/ApiOutlined";
import SchoolOutlinedIcon from "@mui/icons-material/SchoolOutlined";
import TimelineOutlinedIcon from "@mui/icons-material/TimelineOutlined";
import CompareArrowsOutlinedIcon from "@mui/icons-material/CompareArrowsOutlined";
import CheckIcon from "@mui/icons-material/Check";
import servicesBg from "../assets/DAM-Service.png";
import halfCircles from "../assets/half-circles.png";

const gradientHoverSx = {
  position: "relative",
  border: "2px solid transparent",
  backgroundClip: "padding-box",
  transition: "all 0.3s ease",
  "&:hover::before": {
    opacity: 1,
  },
  "&::before": {
    content: '""',
    position: "absolute",
    inset: 0,
    borderRadius: "24px",
    padding: "2px",
    background: "linear-gradient(90deg, #FF7FAC, #5834BA)",
    WebkitMask:
      "linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0)",
    WebkitMaskComposite: "xor",
    maskComposite: "exclude",
    opacity: 0,
    transition: "opacity 0.3s ease",
  },
};

const Services = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <Box sx={{ backgroundColor: "#F9F9F9" }}>
      <Helmet>
        <title>DAM Journey - Services</title>
        <meta
          name="description"
          content="DAM Services for Your Digital Growth, From strategy to implementation and beyond — we help you design, deploy, and optimize Digital Asset Management solutions that scale with your business"
        />
        <meta
          property="keywords"
          content="DAM, DAM Journey, DAM Practice, DAM Consultant, DAM Consulting, DAM Practitioner, DAM Service, DAM Product, DAM Discovery, DAM Implementation, DAM Deployment, DAM Services for Your Digital Growth"
        />
      </Helmet>
      <Box>
        {/* Hero Banner Section */}
        <Box
          sx={{
            width: "100%",
            height: "350px",
            background: "linear-gradient(0deg, #2A2177 0%, #0624BE 100%)",
            display: "flex",
            alignItems: "center",
            px: "84px",
            py: "60px",
            overflow: "hidden",
            position: "relative",
            "@media (max-width: 1200px)": {
              px: "24px",
            },
            "@media (max-width: 780px)": {
              flexDirection: "column",
              textAlign: "center",
              px: "16px",
              pt: "48px",
              pb: 0,
              height: "420px",
            },
          }}
        >
          {/* Left Text Content */}
          <Box
            sx={{
              flex: 2,
              zIndex: 1,
            }}
          >
            <Typography
              variant="h1"
              sx={{
                fontSize: "2.5rem",
                fontWeight: 700,
                color: "#ffffff",
                lineHeight: 1.3,
                mb: 2,
                "@media (max-width: 1200px)": {
                  fontSize: "1.75rem",
                  fontWeight: "semiBold",
                },
                "@media (max-width: 780px)": {
                  fontSize: "1.5rem",
                  mb: 1,
                },
              }}
            >
              DAM Services for Your Digital Growth
            </Typography>
            <Typography
              sx={{
                fontSize: "20px",
                fontWeight: "regular",
                color: "#ffffff",
                lineHeight: 1.7,
                maxWidth: "624px",
                "@media (max-width: 1200px)": {
                  fontSize: "18px",
                },
                "@media (max-width: 780px)": {
                  mb: 2,
                },
              }}
            >
              From strategy to implementation and beyond — we help you
              design, deploy, and optimize Digital Asset Management
              solutions that scale with your business.
            </Typography>
          </Box>

          {/* Right Infographic Image */}
          <Box
            sx={{
              flex: 1,
              display: "flex",
              alignItems: "center",
              justifyContent: "flex-end",
              position: "relative",
            }}
          >
            {/* Background Half Circles */}
            <Box
              component="img"
              src={halfCircles}
              alt=""
              aria-hidden="true"
              sx={{
                position: "absolute",
                width: "599px",
                height: "350px",
                objectFit: "contain",
                left: 50,
                "@media (max-width: 1200px)": {
                  left: 20,
                },
                "@media (max-width: 780px)": {
                  left: "50%",
                  transform: "translateX(-50%)",
                  width: "370px",
                  height: "auto",
                },
                zIndex: 0,
              }}
            />
            {/* Foreground services-bg */}
            <Box
              component="img"
              src={servicesBg}
              // alt="Digital Asset Management Services"
              sx={{
                position: "relative",
                zIndex: 1,
                width: "657px",
                height: "297px",
                "@media (max-width: 1200px)": {
                  width: "372px",
                  height: "232px",
                },
                "@media (max-width: 780px)": {
                  fontSize: "18px",
                  width: "318px",
                  height: "191px",
                },
              }}
            />
          </Box>
        </Box>

        {/* Our Services Section */}
        <Box
          sx={{
            pt: "48px",
            px: "84px",
            "@media (max-width: 1200px)": {
              px: "24px",
            },
            "@media (max-width: 780px)": {
              px: "16px",
            },
          }}
        >
          <Typography
            variant="h2"
            component="h2"
            sx={{
              fontSize: "3rem",
              fontWeight: "bold",
              textAlign: "center",
              color: "#2A2177",
              mb: 2,
              "@media (max-width: 1200px)": {
                fontSize: "2.5rem",
              },
              "@media (max-width: 780px)": {
                fontSize: "2rem",
              },
            }}
          >
            Our Services
          </Typography>
          <Typography
            sx={{
              fontSize: "18px",
              fontWeight: "regular",
              textAlign: "center",
              color: "#666666",
              lineHeight: 1.7,
              mb: 5,
              "@media (max-width: 1200px)": {
                fontSize: "16px",
              },
            }}
          >
            We offer a complete suite of Digital Asset Management
            services designed to support your organization at every
            stage—from discovery and planning to implementation,
            integration, and long-term optimization.
          </Typography>

          {/* Cards */}
          <Box
            sx={{
              display: "flex",
              flexDirection: "row",
              mb: "24px",
              "@media (max-width: 1200px)": {
                flexDirection: "column",
                px: "84px",
              },
              "@media (max-width: 780px)": {
                flexDirection: "column",
                px: "0px",
              },
              gap: "24px",
              justifyContent: "center",
            }}
          >
            {/* Discovery Framework Card */}
            <Box
              sx={{
                flex: 1,
                backgroundColor: "#ffffff",
                borderRadius: "24px",
                pb: "32px",
                pt: "48px",
                px: "32px",
                "@media (max-width: 780px)": {
                  px: "16px",
                  textAlign: "center",
                },
                boxShadow: "0px 2px 12px rgba(0, 0, 0, 0.08)",
                ...gradientHoverSx,
              }}
            >
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "row",
                  "@media (max-width: 780px)": {
                    flexDirection: "column",
                    alignItems: "center",
                    textAlign: "center",
                  },
                  gap: 3,
                  mb: 2,
                }}
              >
                <GradientIcon
                  icon={ManageSearchOutlinedIcon}
                  size={40}
                  bordered
                  boxSize={80}
                  borderRadius={24}
                  borderWidth={2}
                  angle="90"
                  sx={{ mt: 1.25 }}
                />
                <Box
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                    "@media (max-width: 780px)": {
                      textAlign: "left",
                    },
                  }}
                >
                  <Typography
                    sx={{
                      fontSize: "1.5rem",
                      fontWeight: 700,
                      color: "#000000",
                      lineHeight: 2,
                      "@media (max-width: 780px)": {
                        fontSize: "1.25rem",
                      },
                    }}
                  >
                    Discovery Framework
                  </Typography>

                  <Typography
                    sx={{
                      fontSize: "1rem",
                      fontWeight: 400,
                      color: "#666666",
                      lineHeight: 1.6,
                    }}
                  >
                    We begin by understanding your business,
                    content ecosystem, and challenges through
                    structured discovery sessions and detailed
                    DAM questionnaires.
                  </Typography>

                  {/* Rows */}
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "left",
                      py: "16px",
                      color: "#666666",
                      height: "24px",
                      fontSize: "1rem",
                      gap: "8px",
                    }}
                  >
                    <GradientIcon
                      icon={CheckIcon}
                      size={20}
                      boxSize={20}
                      sx={{
                        pt: "3px",
                      }}
                    />
                    Stakeholder interviews
                  </Box>
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "left",
                      py: "16px",
                      color: "#666666",
                      height: "24px",
                      fontSize: "1rem",
                      gap: "8px",
                    }}
                  >
                    <GradientIcon
                      icon={CheckIcon}
                      size={20}
                      boxSize={20}
                      sx={{
                        pt: "3px",
                      }}
                    />
                    Current system analysis
                  </Box>
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "left",
                      py: "16px",
                      color: "#666666",
                      height: "24px",
                      fontSize: "1rem",
                      gap: "8px",
                    }}
                  >
                    <GradientIcon
                      icon={CheckIcon}
                      size={20}
                      boxSize={20}
                      sx={{
                        pt: "3px",
                      }}
                    />
                    Content audit
                  </Box>
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "left",
                      py: "16px",
                      color: "#666666",
                      height: "24px",
                      fontSize: "1rem",
                      gap: "8px",
                    }}
                  >
                    <GradientIcon
                      icon={CheckIcon}
                      size={20}
                      boxSize={20}
                      sx={{
                        pt: "3px",
                      }}
                    />
                    Workflow identification
                  </Box>
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "left",
                      py: "16px",
                      color: "#666666",
                      height: "24px",
                      fontSize: "1rem",
                      gap: "8px",
                    }}
                  >
                    <GradientIcon
                      icon={CheckIcon}
                      size={20}
                      boxSize={20}
                      sx={{
                        pt: "3px",
                      }}
                    />
                    Pain point analysis
                  </Box>
                </Box>
              </Box>
            </Box>

            {/* Vendor Evaluation and RFP Creation Card */}
            <Box
              sx={{
                flex: 1,
                backgroundColor: "#ffffff",
                borderRadius: "24px",
                pb: "32px",
                pt: "48px",
                px: "32px",
                "@media (max-width: 780px)": {
                  px: "16px",
                  textAlign: "center",
                },
                boxShadow: "0px 2px 12px rgba(0, 0, 0, 0.08)",
                ...gradientHoverSx,
              }}
            >
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "row",
                  "@media (max-width: 780px)": {
                    flexDirection: "column",
                    alignItems: "center",
                    textAlign: "center",
                  },
                  gap: 3,
                  mb: 2,
                }}
              >
                <GradientIcon
                  icon={CompareArrowsOutlinedIcon}
                  size={40}
                  bordered
                  boxSize={80}
                  borderRadius={24}
                  borderWidth={2}
                  angle="90"
                  sx={{ mt: 1.25 }}
                />
                <Box
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                    "@media (max-width: 780px)": {
                      textAlign: "left",
                    },
                  }}
                >
                  <Typography
                    sx={{
                      fontSize: "1.5rem",
                      fontWeight: 700,
                      color: "#000000",
                      lineHeight: 2,
                      "@media (max-width: 780px)": {
                        fontSize: "1.25rem",
                      },
                    }}
                  >
                    Vendor Evaluation and RFP Creation
                  </Typography>

                  <Typography
                    sx={{
                      fontSize: "1rem",
                      fontWeight: 400,
                      color: "#666666",
                      lineHeight: 1.6,
                    }}
                  >
                    We help you choose the right DAM platform
                    by creating detailed RFPs and evaluating
                    vendors based on your requirements.
                  </Typography>

                  {/* Rows */}
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "left",
                      py: "16px",
                      color: "#666666",
                      height: "24px",
                      fontSize: "1rem",
                      gap: "8px",
                    }}
                  >
                    <GradientIcon
                      icon={CheckIcon}
                      size={20}
                      boxSize={20}
                      sx={{
                        pt: "3px",
                      }}
                    />
                    Discovery findings vs. product offering
                  </Box>
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "left",
                      py: "16px",
                      color: "#666666",
                      height: "24px",
                      fontSize: "1rem",
                      gap: "8px",
                    }}
                  >
                    <GradientIcon
                      icon={CheckIcon}
                      size={20}
                      boxSize={20}
                      sx={{
                        pt: "3px",
                      }}
                    />
                    RFP creation
                  </Box>
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "left",
                      py: "16px",
                      color: "#666666",
                      height: "24px",
                      fontSize: "1rem",
                      gap: "8px",
                    }}
                  >
                    <GradientIcon
                      icon={CheckIcon}
                      size={20}
                      boxSize={20}
                      sx={{
                        pt: "3px",
                      }}
                    />
                    Vendor comparison matrix and scorecard model
                  </Box>
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "left",
                      py: "16px",
                      color: "#666666",
                      height: "24px",
                      fontSize: "1rem",
                      gap: "8px",
                    }}
                  >
                    <GradientIcon
                      icon={CheckIcon}
                      size={20}
                      boxSize={20}
                      sx={{
                        pt: "3px",
                      }}
                    />
                    Vendor demos
                  </Box>
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "left",
                      py: "16px",
                      color: "#666666",
                      height: "24px",
                      fontSize: "1rem",
                      gap: "8px",
                    }}
                  >
                    <GradientIcon
                      icon={CheckIcon}
                      size={20}
                      boxSize={20}
                      sx={{
                        pt: "3px",
                      }}
                    />
                    Top-5 and final product recommendation
                  </Box>
                </Box>
              </Box>
            </Box>
          </Box>

          <Box
            sx={{
              display: "flex",
              flexDirection: "row",
              mb: "24px",
              "@media (max-width: 1200px)": {
                flexDirection: "column",
                px: "84px",
              },
              "@media (max-width: 780px)": {
                flexDirection: "column",
                px: "0px",
              },
              gap: "24px",
              justifyContent: "center",
            }}
          >
            {/* Asset Lifecycle Management Journey Card */}
            <Box
              sx={{
                flex: 1,
                backgroundColor: "#ffffff",
                borderRadius: "24px",
                pb: "32px",
                pt: "48px",
                px: "32px",
                "@media (max-width: 780px)": {
                  px: "16px",
                  textAlign: "center",
                },
                boxShadow: "0px 2px 12px rgba(0, 0, 0, 0.08)",
                ...gradientHoverSx,
              }}
            >
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "row",
                  "@media (max-width: 780px)": {
                    flexDirection: "column",
                    alignItems: "center",
                    textAlign: "center",
                  },
                  gap: 3,
                  mb: 2,
                }}
              >
                <GradientIcon
                  icon={AutorenewOutlinedIcon}
                  size={40}
                  bordered
                  boxSize={80}
                  borderRadius={24}
                  borderWidth={2}
                  angle="90"
                  sx={{ mt: 1.25 }}
                />
                <Box
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                    "@media (max-width: 780px)": {
                      textAlign: "left",
                    },
                  }}
                >
                  <Typography
                    sx={{
                      fontSize: "1.5rem",
                      fontWeight: 700,
                      color: "#000000",
                      lineHeight: 1.4,
                      my: 1,
                      "@media (max-width: 780px)": {
                        fontSize: "1.25rem",
                      },
                    }}
                  >
                    Asset Lifecycle Management Journey
                  </Typography>

                  <Typography
                    sx={{
                      fontSize: "1rem",
                      fontWeight: 400,
                      color: "#666666",
                      lineHeight: 1.6,
                    }}
                  >
                    Design and define the complete lifecycle of
                    your digital assets from creation to archival.
                  </Typography>

                  {/* Rows */}
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "left",
                      py: "16px",
                      color: "#666666",
                      height: "24px",
                      fontSize: "1rem",
                      gap: "8px",
                    }}
                  >
                    <GradientIcon
                      icon={CheckIcon}
                      size={20}
                      boxSize={20}
                      sx={{
                        pt: "3px",
                      }}
                    />
                    Asset lifecycle mapping
                  </Box>
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "left",
                      py: "16px",
                      color: "#666666",
                      height: "24px",
                      fontSize: "1rem",
                      gap: "8px",
                    }}
                  >
                    <GradientIcon
                      icon={CheckIcon}
                      size={20}
                      boxSize={20}
                      sx={{
                        pt: "3px",
                      }}
                    />
                    Version control strategy
                  </Box>
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "left",
                      py: "16px",
                      color: "#666666",
                      height: "24px",
                      fontSize: "1rem",
                      gap: "8px",
                    }}
                  >
                    <GradientIcon
                      icon={CheckIcon}
                      size={20}
                      boxSize={20}
                      sx={{
                        pt: "3px",
                      }}
                    />
                    Expiry & archival policies
                  </Box>
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "left",
                      py: "16px",
                      color: "#666666",
                      height: "24px",
                      fontSize: "1rem",
                      gap: "8px",
                    }}
                  >
                    <GradientIcon
                      icon={CheckIcon}
                      size={20}
                      boxSize={20}
                      sx={{
                        pt: "3px",
                      }}
                    />
                    Workflow automation
                  </Box>
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "left",
                      py: "16px",
                      color: "#666666",
                      height: "24px",
                      fontSize: "1rem",
                      gap: "8px",
                    }}
                  >
                    <GradientIcon
                      icon={CheckIcon}
                      size={20}
                      boxSize={20}
                      sx={{
                        pt: "3px",
                      }}
                    />
                    Metadata planning
                  </Box>
                </Box>
              </Box>
            </Box>

            {/* DAM Product Implementation and Configuration Card */}
            <Box
              sx={{
                flex: 1,
                backgroundColor: "#ffffff",
                borderRadius: "24px",
                pb: "32px",
                pt: "48px",
                px: "32px",
                "@media (max-width: 780px)": {
                  px: "16px",
                  textAlign: "center",
                },
                boxShadow: "0px 2px 12px rgba(0, 0, 0, 0.08)",
                ...gradientHoverSx,
              }}
            >
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "row",
                  "@media (max-width: 780px)": {
                    flexDirection: "column",
                    alignItems: "center",
                    textAlign: "center",
                  },
                  gap: 3,
                  mb: 2,
                }}
              >
                <GradientIcon
                  icon={SettingsOutlinedIcon}
                  size={40}
                  bordered
                  boxSize={80}
                  borderRadius={24}
                  borderWidth={2}
                  angle="90"
                  sx={{ mt: 1.25 }}
                />
                <Box
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                    "@media (max-width: 780px)": {
                      textAlign: "left",
                    },
                  }}
                >
                  <Typography
                    sx={{
                      fontSize: "1.5rem",
                      fontWeight: 700,
                      color: "#000000",
                      lineHeight: 1.4,
                      my: 1,
                      "@media (max-width: 780px)": {
                        fontSize: "1.25rem",
                      },
                    }}
                  >
                    DAM Product Implementation and Configuration
                  </Typography>

                  <Typography
                    sx={{
                      fontSize: "1rem",
                      fontWeight: 400,
                      color: "#666666",
                      lineHeight: 1.6,
                    }}
                  >
                    We implement and configure your DAM system to
                    align with your workflows, users, and business
                    goals.
                  </Typography>

                  {/* Rows */}
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "left",
                      py: "16px",
                      color: "#666666",
                      height: "24px",
                      fontSize: "1rem",
                      gap: "8px",
                    }}
                  >
                    <GradientIcon
                      icon={CheckIcon}
                      size={20}
                      boxSize={20}
                      sx={{
                        pt: "3px",
                      }}
                    />
                    Platform setup & configuration
                  </Box>
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "left",
                      py: "16px",
                      color: "#666666",
                      height: "24px",
                      fontSize: "1rem",
                      gap: "8px",
                    }}
                  >
                    <GradientIcon
                      icon={CheckIcon}
                      size={20}
                      boxSize={20}
                      sx={{
                        pt: "3px",
                      }}
                    />
                    Metadata schema design
                  </Box>
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "left",
                      py: "16px",
                      color: "#666666",
                      height: "24px",
                      fontSize: "1rem",
                      gap: "8px",
                    }}
                  >
                    <GradientIcon
                      icon={CheckIcon}
                      size={20}
                      boxSize={20}
                      sx={{
                        pt: "3px",
                      }}
                    />
                    User roles & permissions
                  </Box>
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "left",
                      py: "16px",
                      color: "#666666",
                      height: "24px",
                      fontSize: "1rem",
                      gap: "8px",
                    }}
                  >
                    <GradientIcon
                      icon={CheckIcon}
                      size={20}
                      boxSize={20}
                      sx={{
                        pt: "3px",
                      }}
                    />
                    Workflow automation
                  </Box>
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "left",
                      py: "16px",
                      color: "#666666",
                      height: "24px",
                      fontSize: "1rem",
                      gap: "8px",
                    }}
                  >
                    <GradientIcon
                      icon={CheckIcon}
                      size={20}
                      boxSize={20}
                      sx={{
                        pt: "3px",
                      }}
                    />
                    Integration setup
                  </Box>
                </Box>
              </Box>
            </Box>
          </Box>

          <Box
            sx={{
              display: "flex",
              flexDirection: "row",
              mb: "24px",
              "@media (max-width: 1200px)": {
                flexDirection: "column",
                px: "84px",
              },
              "@media (max-width: 780px)": {
                flexDirection: "column",
                px: "0px",
              },
              gap: "24px",
              justifyContent: "center",
            }}
          >
            {/* Governance and Compliance Card */}
            <Box
              sx={{
                flex: 1,
                backgroundColor: "#ffffff",
                borderRadius: "24px",
                pb: "32px",
                pt: "48px",
                px: "32px",
                "@media (max-width: 780px)": {
                  px: "16px",
                  textAlign: "center",
                },
                boxShadow: "0px 2px 12px rgba(0, 0, 0, 0.08)",
                ...gradientHoverSx,
              }}
            >
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "row",
                  "@media (max-width: 780px)": {
                    flexDirection: "column",
                    alignItems: "center",
                    textAlign: "center",
                  },
                  gap: 3,
                  mb: 2,
                }}
              >
                <GradientIcon
                  icon={VerifiedUserOutlinedIcon}
                  size={40}
                  bordered
                  boxSize={80}
                  borderRadius={24}
                  borderWidth={2}
                  angle="90"
                  sx={{ mt: 1.25 }}
                />
                <Box
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                    "@media (max-width: 780px)": {
                      textAlign: "left",
                    },
                  }}
                >
                  <Typography
                    sx={{
                      fontSize: "1.5rem",
                      fontWeight: 700,
                      color: "#000000",
                      lineHeight: 2,
                      "@media (max-width: 780px)": {
                        fontSize: "1.25rem",
                      },
                    }}
                  >
                    Governance and Compliance
                  </Typography>

                  <Typography
                    sx={{
                      fontSize: "1rem",
                      fontWeight: 400,
                      color: "#666666",
                      lineHeight: 1.6,
                    }}
                  >
                    Ensure your DAM system follows structured
                    governance and meets compliance standards.
                  </Typography>

                  {/* Rows */}
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "left",
                      py: "16px",
                      color: "#666666",
                      height: "24px",
                      fontSize: "1rem",
                      gap: "8px",
                    }}
                  >
                    <GradientIcon
                      icon={CheckIcon}
                      size={20}
                      boxSize={20}
                      sx={{
                        pt: "3px",
                      }}
                    />
                    Governance framework
                  </Box>
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "left",
                      py: "16px",
                      color: "#666666",
                      height: "24px",
                      fontSize: "1rem",
                      gap: "8px",
                    }}
                  >
                    <GradientIcon
                      icon={CheckIcon}
                      size={20}
                      boxSize={20}
                      sx={{
                        pt: "3px",
                      }}
                    />
                    Access control policies
                  </Box>
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "left",
                      py: "16px",
                      color: "#666666",
                      height: "24px",
                      fontSize: "1rem",
                      gap: "8px",
                    }}
                  >
                    <GradientIcon
                      icon={CheckIcon}
                      size={20}
                      boxSize={20}
                      sx={{
                        pt: "3px",
                      }}
                    />
                    Compliance standards
                  </Box>
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "left",
                      py: "16px",
                      color: "#666666",
                      height: "24px",
                      fontSize: "1rem",
                      gap: "8px",
                    }}
                  >
                    <GradientIcon
                      icon={CheckIcon}
                      size={20}
                      boxSize={20}
                      sx={{
                        pt: "3px",
                      }}
                    />
                    Audit processes
                  </Box>
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "left",
                      py: "16px",
                      color: "#666666",
                      height: "24px",
                      fontSize: "1rem",
                      gap: "8px",
                    }}
                  >
                    <GradientIcon
                      icon={CheckIcon}
                      size={20}
                      boxSize={20}
                      sx={{
                        pt: "3px",
                      }}
                    />
                    Risk management
                  </Box>
                </Box>
              </Box>
            </Box>

            {/* Stakeholder Onboarding Card */}
            <Box
              sx={{
                flex: 1,
                backgroundColor: "#ffffff",
                borderRadius: "24px",
                pb: "32px",
                pt: "48px",
                px: "32px",
                "@media (max-width: 780px)": {
                  px: "16px",
                  textAlign: "center",
                },
                boxShadow: "0px 2px 12px rgba(0, 0, 0, 0.08)",
                ...gradientHoverSx,
              }}
            >
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "row",
                  "@media (max-width: 780px)": {
                    flexDirection: "column",
                    alignItems: "center",
                    textAlign: "center",
                  },
                  gap: 3,
                  mb: 2,
                }}
              >
                <GradientIcon
                  icon={PersonAddAlt1OutlinedIcon}
                  size={40}
                  bordered
                  boxSize={80}
                  borderRadius={24}
                  borderWidth={2}
                  angle="90"
                  sx={{ mt: 1.25 }}
                />
                <Box
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                    "@media (max-width: 780px)": {
                      textAlign: "left",
                    },
                  }}
                >
                  <Typography
                    sx={{
                      fontSize: "1.5rem",
                      fontWeight: 700,
                      color: "#000000",
                      lineHeight: 2,
                      "@media (max-width: 780px)": {
                        fontSize: "1.25rem",
                      },
                    }}
                  >
                    Stakeholder Onboarding
                  </Typography>

                  <Typography
                    sx={{
                      fontSize: "1rem",
                      fontWeight: 400,
                      color: "#666666",
                      lineHeight: 1.6,
                    }}
                  >
                    We ensure smooth onboarding of all users with
                    proper guidance, documentation, and training.
                  </Typography>

                  {/* Rows */}
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "left",
                      py: "16px",
                      color: "#666666",
                      height: "24px",
                      fontSize: "1rem",
                      gap: "8px",
                    }}
                  >
                    <GradientIcon
                      icon={CheckIcon}
                      size={20}
                      boxSize={20}
                      sx={{
                        pt: "3px",
                      }}
                    />
                    User onboarding strategy
                  </Box>
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "left",
                      py: "16px",
                      color: "#666666",
                      height: "24px",
                      fontSize: "1rem",
                      gap: "8px",
                    }}
                  >
                    <GradientIcon
                      icon={CheckIcon}
                      size={20}
                      boxSize={20}
                      sx={{
                        pt: "3px",
                      }}
                    />
                    Role-based training
                  </Box>
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "left",
                      py: "16px",
                      color: "#666666",
                      height: "24px",
                      fontSize: "1rem",
                      gap: "8px",
                    }}
                  >
                    <GradientIcon
                      icon={CheckIcon}
                      size={20}
                      boxSize={20}
                      sx={{
                        pt: "3px",
                      }}
                    />
                    Documentation
                  </Box>
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "left",
                      py: "16px",
                      color: "#666666",
                      height: "24px",
                      fontSize: "1rem",
                      gap: "8px",
                    }}
                  >
                    <GradientIcon
                      icon={CheckIcon}
                      size={20}
                      boxSize={20}
                      sx={{
                        pt: "3px",
                      }}
                    />
                    Adoption tracking
                  </Box>
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "left",
                      py: "16px",
                      color: "#666666",
                      height: "24px",
                      fontSize: "1rem",
                      gap: "8px",
                    }}
                  >
                    <GradientIcon
                      icon={CheckIcon}
                      size={20}
                      boxSize={20}
                      sx={{
                        pt: "3px",
                      }}
                    />
                    Feedback loops
                  </Box>
                </Box>
              </Box>
            </Box>
          </Box>

          <Box
            sx={{
              display: "flex",
              flexDirection: "row",
              mb: "24px",
              "@media (max-width: 1200px)": {
                flexDirection: "column",
                px: "84px",
              },
              "@media (max-width: 780px)": {
                flexDirection: "column",
                px: "0px",
              },
              gap: "24px",
              justifyContent: "center",
            }}
          >
            {/* Asset Migration Card */}
            <Box
              sx={{
                flex: 1,
                backgroundColor: "#ffffff",
                borderRadius: "24px",
                pb: "32px",
                pt: "48px",
                px: "32px",
                "@media (max-width: 780px)": {
                  px: "16px",
                  textAlign: "center",
                },
                boxShadow: "0px 2px 12px rgba(0, 0, 0, 0.08)",
                ...gradientHoverSx,
              }}
            >
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "row",
                  "@media (max-width: 780px)": {
                    flexDirection: "column",
                    alignItems: "center",
                    textAlign: "center",
                  },
                  gap: 3,
                  mb: 2,
                }}
              >
                <GradientIcon
                  icon={SyncAltOutlinedIcon}
                  size={40}
                  bordered
                  boxSize={80}
                  borderRadius={24}
                  borderWidth={2}
                  angle="90"
                  sx={{ mt: 1.25 }}
                />
                <Box
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                    "@media (max-width: 780px)": {
                      textAlign: "left",
                    },
                  }}
                >
                  <Typography
                    sx={{
                      fontSize: "1.5rem",
                      fontWeight: 700,
                      color: "#000000",
                      lineHeight: 2,
                      "@media (max-width: 780px)": {
                        fontSize: "1.25rem",
                      },
                    }}
                  >
                    Asset Migration
                  </Typography>

                  <Typography
                    sx={{
                      fontSize: "1rem",
                      fontWeight: 400,
                      color: "#666666",
                      lineHeight: 1.6,
                    }}
                  >
                    Seamlessly migrate your existing assets into
                    the new DAM system with accuracy and structure.
                  </Typography>

                  {/* Rows */}
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "left",
                      py: "16px",
                      color: "#666666",
                      height: "24px",
                      fontSize: "1rem",
                      gap: "8px",
                    }}
                  >
                    <GradientIcon
                      icon={CheckIcon}
                      size={20}
                      boxSize={20}
                      sx={{
                        pt: "3px",
                      }}
                    />
                    Asset audit & cleanup
                  </Box>
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "left",
                      py: "16px",
                      color: "#666666",
                      height: "24px",
                      fontSize: "1rem",
                      gap: "8px",
                    }}
                  >
                    <GradientIcon
                      icon={CheckIcon}
                      size={20}
                      boxSize={20}
                      sx={{
                        pt: "3px",
                      }}
                    />
                    Metadata mapping
                  </Box>
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "left",
                      py: "16px",
                      color: "#666666",
                      height: "24px",
                      fontSize: "1rem",
                      gap: "8px",
                    }}
                  >
                    <GradientIcon
                      icon={CheckIcon}
                      size={20}
                      boxSize={20}
                      sx={{
                        pt: "3px",
                      }}
                    />
                    Bulk migration
                  </Box>
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "left",
                      py: "16px",
                      color: "#666666",
                      height: "24px",
                      fontSize: "1rem",
                      gap: "8px",
                    }}
                  >
                    <GradientIcon
                      icon={CheckIcon}
                      size={20}
                      boxSize={20}
                      sx={{
                        pt: "3px",
                      }}
                    />
                    Data validation
                  </Box>
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "left",
                      py: "16px",
                      color: "#666666",
                      height: "24px",
                      fontSize: "1rem",
                      gap: "8px",
                    }}
                  >
                    <GradientIcon
                      icon={CheckIcon}
                      size={20}
                      boxSize={20}
                      sx={{
                        pt: "3px",
                      }}
                    />
                    Quality assurance
                  </Box>
                </Box>
              </Box>
            </Box>

            {/* API Data Services Card */}
            <Box
              sx={{
                flex: 1,
                backgroundColor: "#ffffff",
                borderRadius: "24px",
                pb: "32px",
                pt: "48px",
                px: "32px",
                "@media (max-width: 780px)": {
                  px: "16px",
                  textAlign: "center",
                },
                boxShadow: "0px 2px 12px rgba(0, 0, 0, 0.08)",
                ...gradientHoverSx,
              }}
            >
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "row",
                  "@media (max-width: 780px)": {
                    flexDirection: "column",
                    alignItems: "center",
                    textAlign: "center",
                  },
                  gap: 3,
                  mb: 2,
                }}
              >
                <GradientIcon
                  icon={ApiOutlinedIcon}
                  size={40}
                  bordered
                  boxSize={80}
                  borderRadius={24}
                  borderWidth={2}
                  angle="90"
                  sx={{ mt: 1.25 }}
                />
                <Box
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                    "@media (max-width: 780px)": {
                      textAlign: "left",
                    },
                  }}
                >
                  <Typography
                    sx={{
                      fontSize: "1.5rem",
                      fontWeight: 700,
                      color: "#000000",
                      lineHeight: 2,
                      "@media (max-width: 780px)": {
                        fontSize: "1.25rem",
                      },
                    }}
                  >
                    API Data Services
                  </Typography>

                  <Typography
                    sx={{
                      fontSize: "1rem",
                      fontWeight: 400,
                      color: "#666666",
                      lineHeight: 1.6,
                    }}
                  >
                    Enable seamless integration between DAM and
                    other systems using APIs and data services.
                  </Typography>

                  {/* Rows */}
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "left",
                      py: "16px",
                      color: "#666666",
                      height: "24px",
                      fontSize: "1rem",
                      gap: "8px",
                    }}
                  >
                    <GradientIcon
                      icon={CheckIcon}
                      size={20}
                      boxSize={20}
                      sx={{
                        pt: "3px",
                      }}
                    />
                    API integration
                  </Box>
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "left",
                      py: "16px",
                      color: "#666666",
                      height: "24px",
                      fontSize: "1rem",
                      gap: "8px",
                    }}
                  >
                    <GradientIcon
                      icon={CheckIcon}
                      size={20}
                      boxSize={20}
                      sx={{
                        pt: "3px",
                      }}
                    />
                    Data synchronization
                  </Box>
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "left",
                      py: "16px",
                      color: "#666666",
                      height: "24px",
                      fontSize: "1rem",
                      gap: "8px",
                    }}
                  >
                    <GradientIcon
                      icon={CheckIcon}
                      size={20}
                      boxSize={20}
                      sx={{
                        pt: "3px",
                      }}
                    />
                    Third-party system integration
                  </Box>
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "left",
                      py: "16px",
                      color: "#666666",
                      height: "24px",
                      fontSize: "1rem",
                      gap: "8px",
                    }}
                  >
                    <GradientIcon
                      icon={CheckIcon}
                      size={20}
                      boxSize={20}
                      sx={{
                        pt: "3px",
                      }}
                    />
                    Automation workflows
                  </Box>
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "left",
                      py: "16px",
                      color: "#666666",
                      height: "24px",
                      fontSize: "1rem",
                      gap: "8px",
                    }}
                  >
                    <GradientIcon
                      icon={CheckIcon}
                      size={20}
                      boxSize={20}
                      sx={{
                        pt: "3px",
                      }}
                    />
                    Performance optimization
                  </Box>
                </Box>
              </Box>
            </Box>
          </Box>

          <Box
            sx={{
              display: "flex",
              flexDirection: "row",
              mb: "24px",
              "@media (max-width: 1200px)": {
                flexDirection: "column",
                px: "84px",
              },
              "@media (max-width: 780px)": {
                flexDirection: "column",
                px: "0px",
              },
              gap: "24px",
              justifyContent: "center",
            }}
          >
            {/* DAM Training Card */}
            <Box
              sx={{
                flex: 1,
                backgroundColor: "#ffffff",
                borderRadius: "24px",
                pb: "32px",
                pt: "48px",
                px: "32px",
                "@media (max-width: 780px)": {
                  px: "16px",
                  textAlign: "center",
                },
                boxShadow: "0px 2px 12px rgba(0, 0, 0, 0.08)",
                ...gradientHoverSx,
              }}
            >
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "row",
                  "@media (max-width: 780px)": {
                    flexDirection: "column",
                    alignItems: "center",
                    textAlign: "center",
                  },
                  gap: 3,
                  mb: 2,
                }}
              >
                <GradientIcon
                  icon={SchoolOutlinedIcon}
                  size={40}
                  bordered
                  boxSize={80}
                  borderRadius={24}
                  borderWidth={2}
                  angle="90"
                  sx={{ mt: 1.25 }}
                />
                <Box
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                    "@media (max-width: 780px)": {
                      textAlign: "left",
                    },
                  }}
                >
                  <Typography
                    sx={{
                      fontSize: "1.5rem",
                      fontWeight: 700,
                      color: "#000000",
                      lineHeight: 2,
                      "@media (max-width: 780px)": {
                        fontSize: "1.25rem",
                      },
                    }}
                  >
                    DAM Training
                  </Typography>

                  <Typography
                    sx={{
                      fontSize: "1rem",
                      fontWeight: 400,
                      color: "#666666",
                      lineHeight: 1.6,
                    }}
                  >
                    Empower your team with comprehensive DAM
                    training programs tailored to different user
                    roles.
                  </Typography>

                  {/* Rows */}
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "left",
                      py: "16px",
                      color: "#666666",
                      height: "24px",
                      fontSize: "1rem",
                      gap: "8px",
                    }}
                  >
                    <GradientIcon
                      icon={CheckIcon}
                      size={20}
                      boxSize={20}
                      sx={{
                        pt: "3px",
                      }}
                    />
                    Beginner to advanced training
                  </Box>
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "left",
                      py: "16px",
                      color: "#666666",
                      height: "24px",
                      fontSize: "1rem",
                      gap: "8px",
                    }}
                  >
                    <GradientIcon
                      icon={CheckIcon}
                      size={20}
                      boxSize={20}
                      sx={{
                        pt: "3px",
                      }}
                    />
                    Role-based learning paths
                  </Box>
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "left",
                      py: "16px",
                      color: "#666666",
                      height: "24px",
                      fontSize: "1rem",
                      gap: "8px",
                    }}
                  >
                    <GradientIcon
                      icon={CheckIcon}
                      size={20}
                      boxSize={20}
                      sx={{
                        pt: "3px",
                      }}
                    />
                    Hands-on sessions
                  </Box>
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "left",
                      py: "16px",
                      color: "#666666",
                      height: "24px",
                      fontSize: "1rem",
                      gap: "8px",
                    }}
                  >
                    <GradientIcon
                      icon={CheckIcon}
                      size={20}
                      boxSize={20}
                      sx={{
                        pt: "3px",
                      }}
                    />
                    Documentation & resources
                  </Box>
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "left",
                      py: "16px",
                      color: "#666666",
                      height: "24px",
                      fontSize: "1rem",
                      gap: "8px",
                    }}
                  >
                    <GradientIcon
                      icon={CheckIcon}
                      size={20}
                      boxSize={20}
                      sx={{
                        pt: "3px",
                      }}
                    />
                    Ongoing support
                  </Box>
                </Box>
              </Box>
            </Box>

            {/* Project Management Card */}
            <Box
              sx={{
                flex: 1,
                backgroundColor: "#ffffff",
                borderRadius: "24px",
                pb: "32px",
                pt: "48px",
                px: "32px",
                "@media (max-width: 780px)": {
                  px: "16px",
                  textAlign: "center",
                },
                boxShadow: "0px 2px 12px rgba(0, 0, 0, 0.08)",
                ...gradientHoverSx,
              }}
            >
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "row",
                  "@media (max-width: 780px)": {
                    flexDirection: "column",
                    alignItems: "center",
                    textAlign: "center",
                  },
                  gap: 3,
                  mb: 2,
                }}
              >
                <GradientIcon
                  icon={TimelineOutlinedIcon}
                  size={40}
                  bordered
                  boxSize={80}
                  borderRadius={24}
                  borderWidth={2}
                  angle="90"
                  sx={{ mt: 1.25 }}
                />
                <Box
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                    "@media (max-width: 780px)": {
                      textAlign: "left",
                    },
                  }}
                >
                  <Typography
                    sx={{
                      fontSize: "1.5rem",
                      fontWeight: 700,
                      color: "#000000",
                      lineHeight: 2,
                      "@media (max-width: 780px)": {
                        fontSize: "1.25rem",
                      },
                    }}
                  >
                    Project Management
                  </Typography>

                  <Typography
                    sx={{
                      fontSize: "1rem",
                      fontWeight: 400,
                      color: "#666666",
                      lineHeight: 1.6,
                    }}
                  >
                    We drive your DAM implementation project from
                    start to finish.
                  </Typography>

                  {/* Rows */}
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "left",
                      py: "16px",
                      color: "#666666",
                      height: "24px",
                      fontSize: "1rem",
                      gap: "8px",
                    }}
                  >
                    <GradientIcon
                      icon={CheckIcon}
                      size={20}
                      boxSize={20}
                      sx={{
                        pt: "3px",
                      }}
                    />
                    Risks and dependencies
                  </Box>
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "left",
                      py: "16px",
                      color: "#666666",
                      height: "24px",
                      fontSize: "1rem",
                      gap: "8px",
                    }}
                  >
                    <GradientIcon
                      icon={CheckIcon}
                      size={20}
                      boxSize={20}
                      sx={{
                        pt: "3px",
                      }}
                    />
                    Roadmap
                  </Box>
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "left",
                      py: "16px",
                      color: "#666666",
                      height: "24px",
                      fontSize: "1rem",
                      gap: "8px",
                    }}
                  >
                    <GradientIcon
                      icon={CheckIcon}
                      size={20}
                      boxSize={20}
                      sx={{
                        pt: "3px",
                      }}
                    />
                    Timeline and Milestones
                  </Box>
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "left",
                      py: "16px",
                      color: "#666666",
                      height: "24px",
                      fontSize: "1rem",
                      gap: "8px",
                    }}
                  >
                    <GradientIcon
                      icon={CheckIcon}
                      size={20}
                      boxSize={20}
                      sx={{
                        pt: "3px",
                      }}
                    />
                    Backlog epics/stories creation
                  </Box>
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "left",
                      py: "16px",
                      color: "#666666",
                      height: "24px",
                      fontSize: "1rem",
                      gap: "8px",
                    }}
                  >
                    <GradientIcon
                      icon={CheckIcon}
                      size={20}
                      boxSize={20}
                      sx={{
                        pt: "3px",
                      }}
                    />
                    Daily/Weekly status report
                  </Box>
                </Box>
              </Box>
            </Box>
          </Box>
        </Box>

        {/* Have Questions Section */}
        <HaveQuestions />
      </Box>
    </Box>
  );
};

export default Services;
